import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import type { ProductGroup } from '../../../presentation/catalogue';
import { ECO, ecosystemPhases, layoutGroups, renderEcosystem } from '../../../components/ecosystemMap';
import { draw, pulse } from '../../../components/svg';

const CORE_ORIGIN = `${ECO.cx} ${ECO.cy}`;

/**
 * 0      Moderta alone at the centre of an empty orbit
 * 1..n   one step per phase: the flagship, then platforms and products, then selected work
 * last   the orbit connects, energy flows outward: one growing ecosystem
 * Phases come from the enabled modules, so the step count adapts to the portfolio.
 */
export class EcosystemScene extends BaseScene {
  private readonly phases = ecosystemPhases(this.context);
  private readonly finaleStep = this.phases.length + 1;
  public readonly stepCount = this.phases.length + 2;
  private readonly layout = layoutGroups(this.context.groups);

  private spin?: gsap.core.Tween;
  private pulses: gsap.core.Tween[] = [];

  protected template(): string {
    return `
      <div class="scene-layout ecosystem">
        <svg class="diagram eco-diagram" viewBox="0 0 1600 900" role="img" aria-label="The Moderta ecosystem around its core">
          ${renderEcosystem(this.context, (group) => this.revealStep(group))}
          <g class="eco-statement" data-from="${this.finaleStep}" data-end-anchor>
            <text class="eco-statement__eyebrow" x="${ECO.cx}" y="${ECO.cy - 34}" text-anchor="middle">DIFFERENT PROBLEMS</text>
            <text class="eco-statement__main" x="${ECO.cx}" y="${ECO.cy + 32}" text-anchor="middle">CONNECTED THINKING.</text>
          </g>
        </svg>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    this.ensureSpin();
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    tl.fromTo(this.$('.eco-core'), { opacity: 0, scale: 0.6, svgOrigin: CORE_ORIGIN }, { opacity: 1, scale: 1, duration: 1.3, ease: 'expo.out' }, 0.1);
    tl.fromTo(this.$('.eco-orbit'), { opacity: 0 }, { opacity: 1, duration: 1.6, ease: 'power2.out' }, 0.5);
  }

  protected onExit(): void {
    this.spin = undefined;
    this.pulses = [];
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode('ecosystem', step, immediate);
    this.ensureSpin();

    const forward = !immediate && step > previousStep;
    const tl = this.timeline();
    const d = immediate ? 0 : 1;

    gsap.set(this.$('.eco-orbit'), { opacity: 1 });

    // Worlds and spokes: settle everything, then animate only the groups this step introduces.
    this.context.groups.forEach((group) => {
      const at = this.revealStep(group);
      const visible = step >= at;
      const introduced = forward && at === step;
      const spoke = this.$<SVGPathElement>(`.eco-spoke[data-group="${group.id}"]`);
      const motion = this.$(`.world[data-group="${group.id}"] .world__motion`);
      const motif = this.$$(`.world[data-group="${group.id}"] .motif`);
      if (introduced) return;
      draw(spoke, visible, { immediate: true });
      draw(motif, visible, { immediate: true });
      gsap.set(motion, { x: 0, y: 0, scale: 1, opacity: 1, transformOrigin: '50% 50%' });
    });

    if (forward && step >= 1 && step < this.finaleStep) {
      this.phases[step - 1].forEach((group, i) => this.introduceWorld(tl, group, i * 0.45));
    }

    // The finale: the orbit lights up, the core opens and makes room for the statement.
    const finale = step === this.finaleStep;
    const orbitLit = this.$<SVGPathElement>('.eco-orbit--lit');
    if (forward && finale) tl.add(draw(orbitLit, true, { duration: 2.2, ease: 'power2.inOut' }), 0);
    else draw(orbitLit, finale, { immediate: true });

    gsap.to(this.$('.eco-core__logo'), { opacity: finale ? 0 : 1, duration: d * 0.5, overwrite: 'auto' });
    gsap.to(this.$$('.eco-core__ring, .eco-core__spin'), {
      scale: finale ? 2.5 : 1,
      opacity: finale ? 0.28 : 1,
      svgOrigin: CORE_ORIGIN,
      duration: d * 1.6,
      ease: 'expo.inOut',
      overwrite: 'auto',
    });
    if (forward && finale) {
      tl.fromTo(this.$$('.eco-statement text'), { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 1, stagger: 0.25, ease: 'power3.out' }, 0.9);
    } else {
      gsap.set(this.$$('.eco-statement text'), { opacity: 1, y: 0 });
    }

    this.stopPulses();
    if (finale) this.startPulses(forward ? 1.2 : 0);
  }

  private revealStep(group: ProductGroup): number {
    return this.phases.findIndex((phase) => phase.includes(group)) + 1;
  }

  private introduceWorld(tl: gsap.core.Timeline, group: ProductGroup, at: number): void {
    const anchor = this.layout.get(group.id)!;
    const spoke = this.$<SVGPathElement>(`.eco-spoke[data-group="${group.id}"]`);
    const motion = this.$(`.world[data-group="${group.id}"] .world__motion`);
    const motif = this.$$(`.world[data-group="${group.id}"] .motif`);
    // Each world travels out from the core along its spoke.
    const pull = 0.22;

    draw([spoke, ...motif], false, { immediate: true });
    tl.add(draw(spoke, true, { duration: 0.8, ease: 'power2.out' }), at);
    tl.fromTo(
      motion,
      { x: (ECO.cx - anchor.x) * pull, y: (ECO.cy - anchor.y) * pull, scale: 0.82, opacity: 0, transformOrigin: '50% 50%' },
      { x: 0, y: 0, scale: 1, opacity: 1, duration: 1.2, ease: 'expo.out' },
      at + 0.35,
    );
    tl.add(draw(motif, true, { duration: 1, stagger: 0.15, ease: 'power2.out' }), at + 0.6);
  }

  /** Energy flows from Moderta out to every world, continuously and quietly. */
  private startPulses(delay: number): void {
    this.context.groups.forEach((group, i) => {
      const spoke = this.$<SVGPathElement>(`.eco-spoke[data-group="${group.id}"]`);
      const marker = this.$<SVGCircleElement>(`.eco-pulse[data-group="${group.id}"]`);
      this.pulses.push(this.loop(pulse(marker, spoke, { delay: delay + i * 0.3, repeatDelay: 1.2 })));
    });
  }

  private stopPulses(): void {
    this.pulses.forEach((tween) => tween.kill());
    this.pulses = [];
    this.$$('.eco-pulse').forEach((pulse) => { pulse.style.opacity = '0'; });
  }

  private ensureSpin(): void {
    if (this.spin) return;
    this.spin = this.loop(gsap.to(this.$('.eco-core__spin'), { rotation: '+=360', svgOrigin: CORE_ORIGIN, duration: 70, ease: 'none', repeat: -1 }));
  }
}
